import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Input, Select, Table, Modal, message, Tag, Space, Descriptions, Divider, Tooltip } from 'antd';
import { SellerManagementPageModel } from '../pageModels/SellerManagementPageModel';
import { formatConversionRate, formatMoney, Seller, formatLevel } from '../../models/Seller';
import { globalStore } from '../../models/globalStore';
import { ImagePreview } from '../../components/ImagePreview';
import { CopyOutlined } from '@ant-design/icons';

const model = new SellerManagementPageModel();

const SellerManagement: React.FC = () => {
    useEffect(() => {
        globalStore.selectedMenuKey = 'seller-management';
        model.loadSellers();
    }, []);

    const columns = [
        {
            title: '昵称/ID',
            width: 150,
            key: 'basic',
            render: (_: any, record: Seller) => {
                const records = record.blacklistRecords ?? [];
                const isBlacklisted = records.length > 0 &&
                    records[records.length - 1].type === 0;
                
                return (
                    <div>
                        <div style={{ color: '#222', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {record.nickname}
                            {isBlacklisted && (
                                <span style={{ 
                                    background: '#ff4d4f', 
                                    color: 'white', 
                                    fontSize: '11px', 
                                    padding: '1px 6px', 
                                    borderRadius: '8px',
                                    lineHeight: '1.2',
                                    fontWeight: '500'
                                }}>
                                    已拉黑
                                </span>
                            )}
                        </div>
                        <div style={{ color: '#aaa' }}>{record.id}</div>
                    </div>
                );
            }
        },  
        {
            title: '手机号',
            key: 'phone',
            width: 160,
            render: (_: any, record: Seller) => (
                <div style={{ display: 'flex', alignItems: 'center', width: 120 }}>
                    <span
                        style={{
                            color: '#333',
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {record.phone || '—'}
                    </span>
                    {record.phone ? (
                        <CopyOutlined
                            style={{ marginLeft: 8, color: '#1677ff', cursor: 'pointer', flex: 'none' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard
                                    ?.writeText(record.phone)
                                    .then(() => message.success('已复制'))
                                    .catch(() => message.error('复制失败'));
                            }}
                        />
                    ) : null}
                </div>
            )
        },
        {
            title: '等级',
            key: 'levels',
            render: (_: any, record: Seller) => (
                <div>
                    <div>等级: {formatLevel(record.level)}</div>
                    <div>信用: {record.creditLevelDesc}</div>
                </div>
            )
        },
        {
            title: '咨询/转化',
            key: 'conversion',
            render: (_: any, record: Seller) => (
                <div>
                    <div>咨询数: {record.consultCount}</div>
                    <div>净转化率: {formatConversionRate(record.netConversionRate)}</div>
                    <div>毛转化率: {formatConversionRate(record.grossConversionRate)}</div>
                </div>
            )
        },
        {
            title: '保证金',
            key: 'deposit',
            render: (_: any, record: Seller) => (
                <div>
                    <div>应缴: {formatMoney(record.requiredDeposit)}</div>
                    <div>已缴: {formatMoney(record.actualDeposit)}</div>
                </div>
            )
        },
        {
            title: '未提现金额',
            dataIndex: 'unwithdrawAmount',
            render: (amount: number) => formatMoney(amount)
        }
        // 移除“操作”列
    ];

    const handleConfirmSuperIndividual = (isSuperIndividual: 0 | 1) => {
        Modal.confirm({
            title: `确认${isSuperIndividual ? '授予' : '取消'}超级个体资格？`,
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                if (model.selectedSeller) {
                    model.updateSuperIndividualStatus(model.selectedSeller.id, isSuperIndividual);
                }
            }
        });
    };

    const handleBlacklist = () => {
        model.setShowBlacklistModal(true);
    };

    const handleConfirmBlacklist = (reason: string) => {
        if (model.selectedSeller) {
            const isCurrentlyBlacklisted = model.selectedSeller.blacklistRecords?.length > 0 && 
                model.selectedSeller.blacklistRecords[model.selectedSeller.blacklistRecords.length - 1]?.type === 0;
            model.updateBlacklistStatus(model.selectedSeller.id, isCurrentlyBlacklisted ? 1 : 0, reason);
        }
        model.setShowBlacklistModal(false);
    };

    const getIsCurrentlyBlacklisted = () => {
        if (!model.selectedSeller?.blacklistRecords?.length) return false;
        const lastRecord = model.selectedSeller.blacklistRecords[model.selectedSeller.blacklistRecords.length - 1];
        return lastRecord?.type === 0;
    };

    const isCurrentlyBlacklisted = getIsCurrentlyBlacklisted();

    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewSrc, setPreviewSrc] = useState<string>('');
    const [blacklistRecordsVisible, setBlacklistRecordsVisible] = useState(false);
    const openSignaturePreview = () => {
        if (!model.selectedSeller?.signatureImage) return;
        setPreviewSrc(model.selectedSeller.signatureImage);
        setPreviewVisible(true);
    };
    const closePreview = () => setPreviewVisible(false);

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '30px', paddingLeft: '20px' }}>
                <Space style={{ marginBottom: '15px' }}>
                    <div>筛选：</div>
                    <Input.Search
                        placeholder="搜索卖家手机号/ID/昵称"
                        style={{ width: 350 }}
                        value={model.keyword}
                        onChange={e => model.setKeyword(e.target.value)}
                        onSearch={() => model.loadSellers()}
                    />
                    <Select
                        style={{ width: 120 }}
                        value={model.deposit}
                        onChange={value => {
                            model.setDeposit(value);
                            model.loadSellers();
                        }}
                        options={[
                            { label: '全部保证金', value: 'all' },
                            { label: '完全缴纳', value: 'full' },
                            { label: '部分缴纳', value: 'partial' },
                            { label: '无需缴纳', value: 'none' }
                        ]}
                    />
                    <Select
                        style={{ width: 120 }}
                        value={model.isVerified}
                        onChange={value => {
                            model.setIsVerified(value);
                            model.loadSellers();
                        }}
                        options={[
                            { label: '全部实名', value: 'all' },
                            { label: '已实名', value: 'yes' },
                            { label: '未实名', value: 'no' }
                        ]}
                    />
                    <Select
                        style={{ width: 150 }}
                        value={model.isSuperIndividual}
                        onChange={value => {
                            model.setIsSuperIndividual(value);
                            model.loadSellers();
                        }}
                        options={[
                            { label: '全部卖家', value: 'all' },
                            { label: '是超级个体', value: 'yes' },
                            { label: '非超级个体', value: 'no' }
                        ]}
                    />
                </Space>
                <div style={{ marginTop: '0px',marginBottom: '10px' }}>
                    <Space>
                        <div>排序：</div>
                        <Button onClick={() => model.setSortBy('registerTime')}>
                            注册时间 {model.sortBy === 'registerTime' ? (model.sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                        <Button onClick={() => model.setSortBy('level')}>
                            等级 {model.sortBy === 'level' ? (model.sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                        <Button onClick={() => model.setSortBy('consultCount')}>
                            咨询数 {model.sortBy === 'consultCount' ? (model.sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                        <Button onClick={() => model.setSortBy('orderCount')}>
                            订单数 {model.sortBy === 'orderCount' ? (model.sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                        <Button onClick={() => model.setSortBy('grossConversionRate')}>
                            毛转化率 {model.sortBy === 'grossConversionRate' ? (model.sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                        <Button onClick={() => model.setSortBy('netConversionRate')}>
                            净转化率 {model.sortBy === 'netConversionRate' ? (model.sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                        <Button onClick={() => model.setSortBy('deposit')}>
                            保证金 {model.sortBy === 'deposit' ? (model.sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                        <Button onClick={() => model.setSortBy('unwithdrawAmount')}>
                            未提现金额 {model.sortBy === 'unwithdrawAmount' ? (model.sortOrder === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                        {/* 新增操作按钮 */}
                        <Button type="primary" style={{ marginLeft: '98px', width: '100px' }} onClick={() => model.loadSellers()}>查看</Button>
                        <Button style={{ width: '100px' }} onClick={() => model.resetFilters()}>重置</Button>
                    </Space>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={model.sellers}
                loading={model.loading}
                rowKey="id"
                size="small"
                onRow={(record) => ({
                    onClick: () => model.setSelectedSeller(record)
                })}
                pagination={false}
            />

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <div>{`卖家-${model.selectedSeller?.nickname}${model.selectedSeller ? (model.selectedSeller.status === 'active' ? '（有效）' : '（已注销）') : ''}`}</div>
                        <Space style={{ marginLeft: 20 }}>
                            <Button
                                type="primary"
                                onClick={() => handleConfirmSuperIndividual(model.selectedSeller?.isSuperIndividual === 1 ? 0 : 1)}
                            >
                                {model.selectedSeller?.isSuperIndividual === 1 ? '取消超级个体' : '授予超级个体'}
                            </Button>
                            <Button
                                type={isCurrentlyBlacklisted ? 'primary' : 'default'}
                                danger
                                onClick={handleBlacklist}
                            >
                                {isCurrentlyBlacklisted ? '取消拉黑' : '拉黑'}
                            </Button>
                            <Button type="link" style={{ padding: 0 }} onClick={() => setBlacklistRecordsVisible(true)}>
                                查看拉黑记录
                            </Button>
                        </Space>
                    </div>
                }
                className="seller-detail-modal"
                open={model.showDetailModal}
                onCancel={() => model.setSelectedSeller(null)}
                footer={null}
                width={860}
            >
                {model.selectedSeller && (
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 100, margin: '2px 0 6px' }}>基本信息</div>
                        <Descriptions bordered column={4} size="small" labelStyle={{ padding: '4px 6px', fontSize: 12 }} contentStyle={{ padding: '4px 6px', fontSize: 12 }}>
                            <Descriptions.Item label="ID">{model.selectedSeller.id}</Descriptions.Item>
                            <Descriptions.Item label="昵称">{model.selectedSeller.nickname}</Descriptions.Item>
                            <Descriptions.Item label="姓名">{model.selectedSeller.realName}</Descriptions.Item>
                            <Descriptions.Item label="手机号">{model.selectedSeller.phone}</Descriptions.Item> 
                            <Descriptions.Item label="超级个体">{model.selectedSeller.isSuperIndividual === 1 ? '是' : '否'}</Descriptions.Item>
                            <Descriptions.Item label="微信号">{model.selectedSeller.wechat}</Descriptions.Item>
                            <Descriptions.Item label="抖音号">{model.selectedSeller.douyin}</Descriptions.Item>
                            <Descriptions.Item label="实名状态">{model.selectedSeller.isVerified ? '已实名' : '未实名'}</Descriptions.Item> 
                            <Descriptions.Item label="注册时间">{new Date(model.selectedSeller.registerTime).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="签名">
                                {model.selectedSeller.signatureImage ? (
                                    <a onClick={openSignaturePreview} style={{ color: '#1677ff' }}>查看签名</a>
                                ) : '无'}
                            </Descriptions.Item>
                        </Descriptions>

                        <div style={{ fontSize: 14, fontWeight: 100, margin: '2px 0 6px', marginTop: '10px' }}>经营数据</div>
                        <Descriptions bordered column={5} size="small" layout="vertical" labelStyle={{ padding: '0 0px', fontSize: 12 }} contentStyle={{ padding: '2px 6px', fontSize: 12, lineHeight: 1.1 }}>
                            <Descriptions.Item label="咨询数">{model.selectedSeller.consultCount}</Descriptions.Item>
                            <Descriptions.Item label="成交客户数">{model.selectedSeller.dealCustomerCount}</Descriptions.Item>
                            <Descriptions.Item label="订单数">{model.selectedSeller.orderCount}</Descriptions.Item>
                            <Descriptions.Item label="咨询净转化率">{formatConversionRate(model.selectedSeller.netConversionRate)}</Descriptions.Item>
                            <Descriptions.Item label="咨询毛转化率">{formatConversionRate(model.selectedSeller.grossConversionRate)}</Descriptions.Item>
                        </Descriptions>
 
 

                        <div style={{ fontSize: 14, fontWeight: 100, margin: '2px 0 6px', marginTop: '10px' }}>账户和等级</div>
                        <Descriptions bordered column={5} size="small" layout="vertical" labelStyle={{ padding: '0 6px', fontSize: 12 }} contentStyle={{ padding: '2px 6px', fontSize: 12, lineHeight: 1.1 }}>
                        <Descriptions.Item label="未提现金额">{formatMoney(model.selectedSeller.unwithdrawAmount)}</Descriptions.Item>
                            <Descriptions.Item label="应缴保证金">{formatMoney(model.selectedSeller.requiredDeposit)}</Descriptions.Item>
                            <Descriptions.Item label="已缴保证金">{formatMoney(model.selectedSeller.actualDeposit)}</Descriptions.Item> 
                            <Descriptions.Item label="等级">{formatLevel(model.selectedSeller.level)}</Descriptions.Item>
                            <Descriptions.Item label="信用等级">{model.selectedSeller.creditLevelDesc}</Descriptions.Item>
                        </Descriptions>
 

                       

                        {/* 拉黑记录已移至单独弹框查看 */}
                    </div>
                )}
            </Modal>

            <ImagePreview visible={previewVisible} src={previewSrc} title="签名预览" onClose={closePreview} />
            <Modal
                title={isCurrentlyBlacklisted ? '取消拉黑' : '拉黑卖家'}
                open={model.showBlacklistModal}
                onCancel={() => model.setShowBlacklistModal(false)}
                onOk={() => {
                    const input = document.getElementById('blacklistReason') as HTMLInputElement;
                    if (!input.value) {
                        message.error('请输入原因');
                        return;
                    }
                    handleConfirmBlacklist(input.value);
                }}
            >
                <Input.TextArea
                    id="blacklistReason"
                    placeholder="请输入原因"
                    rows={4}
                />
            </Modal>

            <Modal
                title="拉黑记录"
                open={blacklistRecordsVisible}
                onCancel={() => setBlacklistRecordsVisible(false)}
                footer={null}
                width={720}
            >
                {model.selectedSeller ? (
                    !(model.selectedSeller.blacklistRecords?.length) ? (
                        <div style={{ color: '#999' }}>暂无记录</div>
                    ) : (
                        <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 6 }}>
                            {model.selectedSeller.blacklistRecords.map((record, index) => (
                                <div key={index} style={{ padding: '4px 6px', background: index % 2 === 0 ? '#fafafa' : '#fff', border: '1px solid #f0f0f0', borderRadius: 0, marginBottom: 6, fontSize: 12 }}>
                                    <Space size={12}>
                                        <span>操作：{record.type === 0 ? '拉黑' : '取消拉黑'}</span>
                                        <span>原因：{record.reason}</span>
                                        <span>时间：{new Date(record.timestamp).toLocaleString()}</span>
                                    </Space>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <div style={{ color: '#999' }}>暂无数据</div>
                )}
            </Modal>
        </div>
    );
};

export default observer(SellerManagement); 